





















  import Vue from 'vue';
  import { Component, Prop, Watch } from 'vue-property-decorator';
  import StaggeredFadeAndSlide from '../../../components/animations/staggered-fade-and-slide.vue';
  import { xComponentMixin } from '../../../components/x-component.mixin';
  import { RequestStatus } from '../../../store';
  import { queriesPreviewXModule } from '../x-module';
  import { QueryPreviewInfo } from '../store/types';
  import QueryPreview from './query-preview.vue';

  interface QueryPreviewStatusRecord {
    [query: string]: RequestStatus;
  }

  /**
   * Renders the results previews of a list of objects with the information about the query preview,
   * and exposes the {@link QueryPreview} slots to modify the content.
   * The requests are made in sequential order.
   *
   * @public
   */
  @Component({
    inheritAttrs: false,
    components: {
      QueryPreview,
      StaggeredFadeAndSlide
    },
    mixins: [xComponentMixin(queriesPreviewXModule)]
  })
  export default class QueryPreviewList extends Vue {
    /**
     * Animation component that will be used to animate the elements.
     *
     * @public
     */
    @Prop({ default: 'ul' })
    public animation!: Vue | string;

    /**
     * The list of queries preview to render.
     *
     * @public
     */
    @Prop({ required: true })
    public queriesPreviewInfo!: QueryPreviewInfo[];

    /**
     * Contains the status of the preview requests, indexed by query.
     */
    public queriesStatus: QueryPreviewStatusRecord = {};

    /**
     * The list of queries to preview.
     *
     * @returns The list of queries in the queriesPreviewInfo list.
     * @internal
     */
    protected get queries(): string[] {
      return this.queriesPreviewInfo.map(item => item.query);
    }

    /**
     * Gets all the queries to render, that are those that don't have an `error` status.
     *
     * @returns A list of queries.
     * @internal
     */
    protected get renderedQueryPreviews(): QueryPreviewInfo[] {
      return this.queriesPreviewInfo.filter(
        ({ query }) =>
          this.queriesStatus[query] === 'success' || this.queriesStatus[query] === 'loading'
      );
    }

    /**
     * Resets the status of all queries if they change.
     *
     * @param newQueries - The new queries.
     * @param oldQueries - The old queries.
     * @internal
     */
    @Watch('queries', { immediate: true })
    protected resetStatusRecord(newQueries: string[], oldQueries: string[]): void {
      if (newQueries?.toString() !== oldQueries?.toString()) {
        this.queriesStatus = {};
        this.loadNext();
      }
    }

    /**
     * Sets the status of a given query to `success`.
     *
     * @param loadedQuery - The query to flag as loaded.
     * @internal
     */
    protected flagAsLoaded(loadedQuery: string): void {
      this.queriesStatus[loadedQuery] = 'success';
      this.loadNext();
    }

    /**
     * Sets the status of a given query to `error`.
     *
     * @param failedQuery - The query to flag as failed.
     * @internal
     */
    protected flagAsFailed(failedQuery: string): void {
      this.queriesStatus[failedQuery] = 'error';
      this.loadNext();
    }

    /**
     * Tries to load the next query.
     *
     * @internal
     */
    protected loadNext(): void {
      const queryToLoad = this.queries.find(query => !(query in this.queriesStatus));
      if (queryToLoad) {
        this.$set(this.queriesStatus, queryToLoad, 'loading');
      }
    }
  }
